import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PostAdd as PostAddIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Lightbulb as LightbulbIcon,
  Search as SearchIcon,
  Forum as ForumIcon,
  Group as GroupIcon,
  Whatshot as FlareIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { FEATURES } from '../constants/features';

const NAV_ITEMS = [
  { to: '/app', label: 'Dashboard', icon: <DashboardIcon /> },
  { to: '/app/post', label: 'Manage Posts', icon: <PostAddIcon />, feature: FEATURES.POSTING },
  { to: '/app/past-posts', label: 'Past Posts', icon: <HistoryIcon />, feature: FEATURES.POSTING },
  { to: '/app/analyze', label: 'Analyze', icon: <AnalyticsIcon />, feature: FEATURES.AI_GENERATION },
  { to: '/app/rank', label: 'Rankings', icon: <TrendingUpIcon />, feature: FEATURES.ANALYTICS },
  { to: '/app/trends', label: 'Trending Now', icon: <FlareIcon />, feature: FEATURES.ANALYTICS },
  { to: '/app/gaps', label: 'Find Gaps', icon: <SearchIcon />, feature: FEATURES.AI_GENERATION },
  { to: '/app/ideas', label: 'Ideas', icon: <LightbulbIcon />, feature: FEATURES.AI_GENERATION },
  { to: '/app/engagement', label: 'Engagement', icon: <ForumIcon />, dividerBefore: true, feature: FEATURES.ENGAGEMENT },
  { to: '/app/crm', label: 'Lead CRM', icon: <GroupIcon />, feature: FEATURES.LEADS_CRM },
];

const Navigation = ({ isSidebar = false, onCloseDrawer }) => {
  const location = useLocation();
  const theme = useTheme();
  const { isFeatureEnabled } = useFeatureToggles();

  const isActive = (to) => {
    if (to === '/app') return location.pathname === '/app';
    // Calendar view is a tab of Manage Posts — keep it highlighted
    if (to === '/app/post' && location.pathname === '/app/calendar') return true;
    return location.pathname.startsWith(to);
  };

  return (
    <List sx={{ px: 0, py: 0 }}>
      {NAV_ITEMS.map(({ to, label, icon, dividerBefore, feature }) => {
        if (feature && !isFeatureEnabled(feature)) return null;

        const active = isActive(to);

        return (
          <React.Fragment key={to}>
            {dividerBefore && isSidebar && (
              <Box sx={{ my: 3, mx: 2, height: '1px', bgcolor: alpha(theme.palette.divider, 0.05) }} />
            )}

            <ListItem disablePadding sx={{ mb: 1 }}>
              <Tooltip title={isSidebar ? '' : label} placement="right" arrow>
                <ListItemButton
                  component={Link}
                  to={to}
                  onClick={onCloseDrawer}
                  sx={{
                    borderRadius: '12px',
                    mx: isSidebar ? 0 : 0.5,
                    px: 2,
                    py: 1.5,
                    position: 'relative',
                    background: active
                      ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 100%)`
                      : 'transparent',
                    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.primary.main,
                      transform: 'translateX(4px)',
                    },
                    '&::before': active && isSidebar ? {
                      content: '""',
                      position: 'absolute',
                      left: -12,
                      top: '15%',
                      height: '70%',
                      width: 4,
                      bgcolor: theme.palette.primary.main,
                      borderRadius: '0 4px 4px 0',
                      boxShadow: `4px 0 12px ${alpha(theme.palette.primary.main, 0.5)}`,
                    } : {},
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: active ? theme.palette.primary.main : 'inherit',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {React.cloneElement(icon, { sx: { fontSize: 24 } })}
                  </ListItemIcon>

                  {isSidebar && (
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{
                        fontWeight: active ? 800 : 600,
                        fontSize: '0.95rem',
                        letterSpacing: '-0.01em',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default Navigation;
